import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import useTwilioCall from 'hooks/useTwilioCall';
import { useSelector } from 'store';
import { isNoteGenerationInProgress } from 'store/slices/twilioOutboundCall';

// /clinic/{id}/patient/{id} prefix of a pathname, or null if not a patient page.
const patientPrefixOf = (pathname) => pathname.match(/^\/clinic\/[^/]+\/patient\/[^/]+/)?.[0] ?? null;

// Blocks leaving the patient page in two cases (never concurrent, so one blocker):
//   - call mode: active call — forbidden, "Stay" only.
//   - note mode: note still generating — allowed after confirming ("Leave anyway").
// Nav within the patient (tab switches) is always allowed.
const PatientNavigationGuard = () => {
    const { isActive } = useTwilioCall();
    const generatedNote = useSelector((state) => state.twilioOutboundCall.generatedNote);
    const noteGenerating = isNoteGenerationInProgress(generatedNote);
    // Call wins if both are somehow true — it's the stricter guard.
    let mode = null;
    if (isActive) {
        mode = 'call';
    } else if (noteGenerating) {
        mode = 'note';
    }

    const shouldBlock = useCallback(
        ({ currentLocation, nextLocation }) => {
            if (!mode) {
                return false;
            }
            if (currentLocation.pathname === nextLocation.pathname) {
                return false;
            }
            const patientPrefix = patientPrefixOf(currentLocation.pathname);
            if (!patientPrefix) {
                return false;
            }
            if (nextLocation.pathname === patientPrefix || nextLocation.pathname.startsWith(`${patientPrefix}/`)) {
                return false;
            }
            return true;
        },
        [mode]
    );

    const blocker = useBlocker(shouldBlock);

    // Guard cleared mid-navigation (call ended / note settled) — let it through.
    useEffect(() => {
        if (blocker.state === 'blocked' && !mode) {
            blocker.proceed();
        }
    }, [blocker, mode]);

    // useBlocker only catches SPA nav; beforeunload covers tab close / reload.
    useEffect(() => {
        if (!mode) {
            return undefined;
        }
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = ''; // required for Chrome to show its native prompt
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [mode]);

    const open = blocker.state === 'blocked';

    const onStay = () => {
        blocker.reset();
    };

    const onLeave = () => {
        blocker.proceed();
    };

    if (mode === 'note') {
        return (
            <Dialog open={open} onClose={onStay} aria-labelledby="patient-nav-guard-title">
                <DialogTitle id="patient-nav-guard-title">Leave before your note is ready?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Your generated note will be gone if you leave the page. Are you sure?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onLeave} color="inherit">
                        Leave anyway
                    </Button>
                    <Button onClick={onStay} autoFocus variant="contained">
                        Stay on patient
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onStay} aria-labelledby="patient-nav-guard-title">
            <DialogTitle id="patient-nav-guard-title">You&apos;re on an active call</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You can&apos;t navigate away from this patient while a call is in progress. Hang up the call first, or stay here and
                    continue.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onStay} autoFocus variant="contained">
                    Stay on patient
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PatientNavigationGuard;
